import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    inject
} from '@angular/core';

import { BehaviorSubject, NEVER } from 'rxjs';
import { catchError, distinctUntilChanged, take, takeUntil } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { BaseContainer } from '@stylehaven/components';
import { isEqual } from 'lodash-es';
import { ExtractMediaObjectId } from '../../../helpers';
import { ApiFragment } from '../../../record/entities/api-fragment.entity';
import { ScannerInputComponent } from '../../components/scanner-input/scanner-input.component';
import { ScanItemValidationOptions } from '../../entities/scan-item-validation-options.entity';
import { ScanItem, ScanItemState } from '../../entities/scan-item.entity';
import { ScanItemValidationService } from '../../services/scan-item-validation.service';
import { ScanSandbox } from '../../services/scan.sandbox';

@Component({
    standalone: false,
    selector: 'digihaven-multiple-records-scanner',
    templateUrl: './multiple-records-scanner.container.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultipleRecordsScannerContainer extends BaseContainer implements OnInit, OnDestroy {
    @Input() initialRecordIds: string[] = [];
    @Input() validationOptions: ScanItemValidationOptions;

    @Output() validRecords: EventEmitter<ScanItem[]> = new EventEmitter();
    @Output() allRecordsValid: EventEmitter<boolean> = new EventEmitter();

    readonly scanItems$: BehaviorSubject<ScanItem[]> = new BehaviorSubject<ScanItem[]>([]);

    private scanItemValidationService: ScanItemValidationService = inject(ScanItemValidationService);

    @ViewChild(ScannerInputComponent)
    scannerInput!: ScannerInputComponent;

    constructor(private readonly sandbox: ScanSandbox) {
        super();
    }

    ngOnInit(): void {
        this.scanItems$
            .pipe(distinctUntilChanged(isEqual), takeUntil(this.destroy$))
            .subscribe(sanItems => this.emitScannedRecords(sanItems));
        this.setInitialItems();
    }

    private setInitialItems() {
        const initialItems: ScanItem[] = this.initialRecordIds.map(id => ({
            id: ExtractMediaObjectId(id),
            state: ScanItemState.Initial
        }));
        this.scanItems$.next(initialItems);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.validRecords.complete();
        this.scanItems$.complete();
    }

    onScanned(scanItem: ScanItem): void {
        this.addScanItem(scanItem);

        this.sandbox
            .getRecordById(scanItem.id)
            .pipe(
                take(1),
                takeUntil(this.destroy$),
                catchError(err => this.handleScanError(scanItem, err))
            )
            .subscribe(record => this.handleScanCompleted(record, scanItem));
    }

    private addScanItem(item: ScanItem): void {
        this.scanItems$.next([...this.scanItems$.value, item]);
    }

    private emitScannedRecords(scanItems: ScanItem[]) {
        const validRecords = scanItems.filter(
            value => value.state === ScanItemState.Accepted || value.state === ScanItemState.Initial
        );
        this.validRecords.next(validRecords);
        const rejectedRecords = scanItems.filter(value => value.state === ScanItemState.Rejected);
        this.allRecordsValid.emit(
            scanItems.length > 0 &&
                scanItems.every(item => [ScanItemState.Accepted, ScanItemState.Initial].includes(item.state)) &&
                scanItems.some(item => item.state === ScanItemState.Accepted) &&
                rejectedRecords.length === 0
        );
    }

    private handleScanError(scanItem: ScanItem, response: HttpErrorResponse) {
        const errorMsg =
            response.error.Status === 404
                ? this.translateService.instant('scan.form.error.not-found')
                : this.translateService.instant('scan.form.error.unknown');

        const scanError = {
            state: ScanItemState.Rejected,
            errorMsg
        };
        this.updateScanItem(scanItem.id, scanError);
        return NEVER;
    }

    private handleScanCompleted(record: ApiFragment, scanItem: ScanItem) {
        const itemToValidate = {
            ...scanItem,
            record
        };
        const validatedItem = this.scanItemValidationService.validate(itemToValidate, this.validationOptions);
        this.updateScanItem(scanItem.id, validatedItem);
    }

    private updateScanItem(id: string, item: Partial<ScanItem>): void {
        const newScanItems = this.scanItems$.value.map(toUpdate => {
            if (toUpdate.id === id) {
                return { ...toUpdate, ...item, id };
            } else {
                return toUpdate;
            }
        });

        this.scanItems$.next(newScanItems);
    }

    onRemoveScanItem(id: string): void {
        const items: ScanItem[] = this.scanItems$.value.filter(item => item.id !== id);
        this.scanItems$.next(items);
    }

    focus() {
        this.scannerInput.focus();
    }
}
